import { useContext, useState } from "react";
import { message } from "antd";
import {
  ACTIVEDOMAIN,
  FacilityID,
  ORGANISATONID,
  Role,
} from "../../constants/defaultKeys";
import { redirectToDashboard } from "./helper";
import { setCookie } from "../localStorage";
import { UserContext } from "../../context/UserContext";
import { setOneSignalUser } from "../../onesignal";
import { updateUserApi } from "../../services/auth.services";

const isPartnerOnlyOrg = (org) =>
  org?.roles?.length === 1 && org?.roles?.[0]?.role === "Partner";

const getOrgIdForPolicyUpdate = (data, orgID) => {
  if (orgID) return orgID;

  const organizations = data?.organizations || [];
  if (!organizations.length) return null;
  if (organizations.length === 1) return organizations[0]?.org_id;

  const preferredOrg = organizations.find((org) => !isPartnerOnlyOrg(org));
  return preferredOrg?.org_id ?? organizations[0]?.org_id;
};

const applyOrgCookiesAndRedirect = (activeOrg, orgId) => {
  setCookie(
    ORGANISATONID,
    orgId ?? activeOrg?.org_id,
    `.${process.env.REACT_APP_PUBLIC_DOMAIN}`,
    "/"
  );
  setCookie(
    FacilityID,
    activeOrg?.roles?.[0]?.facility_id,
    `.${process.env.REACT_APP_PUBLIC_DOMAIN}`,
    "/"
  );
  setCookie(
    Role,
    activeOrg?.roles?.[0]?.role,
    `.${process.env.REACT_APP_PUBLIC_DOMAIN}`,
    "/"
  );

  if (activeOrg?.domain) {
    setCookie(
      ACTIVEDOMAIN,
      activeOrg?.domain,
      `.${process.env.REACT_APP_PUBLIC_DOMAIN}`,
      "/"
    );
    redirectToDashboard(
      activeOrg?.domain,
      activeOrg?.roles?.[0]?.role,
      activeOrg?.roles?.[0]
    );
  }
};

export const useLoginUserDetails = (orgID) => {
  const { setUserDetails } = useContext(UserContext);
  const [openOrgModal, setOpenOrgModal] = useState(false);
  const [openTermsModal, setOpenTermsModal] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [pendingUserData, setPendingUserData] = useState(null);

  const continueLoginFlow = (data) => {
    const oneSignalId = data?.onesignal_player_id;

    if (orgID) {
      setOneSignalUser(oneSignalId);
      const activeOrg = data?.organizations?.filter(
        (org) => org?.org_id == orgID
      )?.[0];
      setUserDetails({
        ...data,
        activeOrg,
        currFacility: activeOrg?.roles?.[0]?.facility_id,
        currRole: activeOrg?.roles?.[0]?.role,
      });
      applyOrgCookiesAndRedirect(activeOrg, orgID);
      return;
    }

    if (data?.organizations?.length === 1) {
      setOneSignalUser(oneSignalId);
      const orgId = data?.organizations?.[0].org_id;
      const activeOrg = orgId
        ? data?.organizations?.filter((org) => org?.org_id == orgId)?.[0]
        : {};
      setUserDetails({
        ...data,
        activeOrg,
        currFacility: activeOrg?.roles?.[0]?.facility_id,
        currRole: activeOrg?.roles?.[0]?.role,
      });
      applyOrgCookiesAndRedirect(activeOrg, orgId);
      return;
    }

    setOpenOrgModal(true);
    setUserDetails({ ...data });
  };

  const handleUserDetailsResponse = (data) => {
    if (!data) return;

    if (!data.policy_consent) {
      setPendingUserData(data);
      setOpenTermsModal(true);
      return;
    }

    continueLoginFlow(data);
  };

  const handleTermsAccepted = async () => {
    const userId = pendingUserData?.id ?? pendingUserData?.user_id;
    const orgId = getOrgIdForPolicyUpdate(pendingUserData, orgID);

    if (!userId) {
      message.error("Unable to save policy consent. User details are missing.");
      return;
    }

    if (!orgId) {
      message.error("Unable to save policy consent. Organization not found.");
      return;
    }

    setAcceptingTerms(true);
    try {
      await updateUserApi(
        { user_id: userId, policy_consent: true },
        { headers: { Organization: String(orgId) } }
      );
      setOpenTermsModal(false);
      continueLoginFlow({
        ...pendingUserData,
        policy_consent: true,
      });
      setPendingUserData(null);
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "Failed to save policy consent. Please try again."
      );
    } finally {
      setAcceptingTerms(false);
    }
  };

  const closeOrgModal = () => setOpenOrgModal(false);

  return {
    handleUserDetailsResponse,
    openOrgModal,
    closeOrgModal,
    openTermsModal,
    handleTermsAccepted,
    acceptingTerms,
  };
};
